"use client";

import React from "react";
import { Bed, Bath, Maximize, MapPin } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const properties = [{
  id: "prop-1",
  address: "The Skyline Loft, Downtown",
  price: "$3,200/mo",
  beds: 1,
  baths: 1,
  sqft: "850",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686159/img/urban_living_skyline_loft.png",
  imageSrcSet: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686159/img/urban_living_skyline_loft.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686159/img/urban_living_skyline_loft.png-wvc-sizes",
  link: "/ResidencesPage"
}, {
  id: "prop-2",
  address: "Royal Blue Terrace, Midtown",
  price: "$4,850/mo",
  beds: 2,
  baths: 2,
  sqft: "1,200",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686159/img/urban_living_midtown_terrace.png",
  imageSrcSet: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686159/img/urban_living_midtown_terrace.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686159/img/urban_living_midtown_terrace.png-wvc-sizes",
  link: "/ResidencesPage"
}, {
  id: "prop-3",
  address: "The Penthouse at Azure",
  price: "$7,500/mo",
  beds: 3,
  baths: 3,
  sqft: "2,100",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686159/img/urban_living_azure_penthouse.png",
  imageSrcSet: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686159/img/urban_living_azure_penthouse.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686159/img/urban_living_azure_penthouse.png-wvc-sizes",
  link: "/ResidencesPage"
}, {
  id: "prop-4",
  address: "Harbor View Suites, Waterfront",
  price: "$3,900/mo",
  beds: 2,
  baths: 1,
  sqft: "1,050",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686159/img/urban_living_harbor_view.png",
  imageSrcSet: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686159/img/urban_living_harbor_view.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686159/img/urban_living_harbor_view.png-wvc-sizes",
  link: "/ResidencesPage"
}];
export default function AllAvailableUnits() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="262417" id="all-available-units" className="w-full bg-background py-16 md:py-24 overflow-hidden" ref={ref}>
      <div className={`container mx-auto px-4 md:px-6 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-6">
          <div className="max-w-2xl">
            <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-bold text-foreground tracking-tighter mb-4">
              All Available Units
            </h2>
            <p className="text-muted-foreground text-lg font-default">
              Explore our curated selection of premium apartments at Urban Living Rentals, where architectural elegance meets modern comfort.
            </p>
          </div>
          <div className="hidden md:block">
            <Button asChild variant="outline" className="rounded-md border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-colors duration-300">
              <Link to="/ResidencesPage">View All Residences</Link>
            </Button>
          </div>
        </div>

        <Carousel opts={{
        align: "start",
        loop: true
      }} className="w-full">
          <CarouselContent className="-ml-4 md:-ml-6">
            {properties.map((property, index) => <CarouselItem key={property.id} data-index={index} className="pl-4 md:pl-6 md:basis-1/2 lg:basis-1/3">
                <Card className="group relative overflow-hidden border-border bg-card shadow-sm hover:shadow-xl transition-all duration-400 h-full flex flex-col rounded-md">
                  <div className="relative overflow-hidden">
                    <AspectRatio ratio={4 / 3}>
                      <img src={property.imageSrc} data-wvc-srcset={property.imageSrcSet} data-wvc-sizes={property.imageSizes} alt={`Apartment at ${property.address}`} className="object-cover w-full h-full transition-transform duration-700 ease-in-out group-hover:scale-105" loading="lazy" />
                    </AspectRatio>
                    
                    <Badge className="absolute top-4 right-4 bg-primary text-primary-foreground shadow-md px-3 py-1 text-sm font-bold z-10 rounded-md">
                      {property.price}
                    </Badge>

                    <div className="absolute inset-0 bg-primary/20 backdrop-blur-[1px] opacity-0 group-hover:opacity-100 transition-opacity duration-400 flex items-center justify-center z-20">
                      <Button asChild variant="secondary" className="translate-y-8 group-hover:translate-y-0 transition-transform duration-400 ease-out shadow-lg bg-background text-primary hover:bg-primary hover:text-primary-foreground rounded-md">
                        <Link to={property.link}>Schedule Tour</Link>
                      </Button>
                    </div>
                  </div>

                  <CardContent className="p-6 flex-grow flex flex-col justify-between bg-card relative z-10">
                    <div>
                      <div className="flex items-start gap-2 mb-3">
                        <MapPin className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                        <h3 className="font-default text-xl font-bold text-foreground leading-tight line-clamp-2 tracking-tight">
                          {property.address}
                        </h3>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4 pt-4 mt-4 border-t border-border">
                      <div className="flex flex-col items-center justify-center text-center">
                        <Bed className="w-5 h-5 text-primary mb-1" />
                        <span className="text-sm font-semibold text-foreground">{property.beds}</span>
                        <span className="text-xs text-muted-foreground font-default">Beds</span>
                      </div>
                      <div className="flex flex-col items-center justify-center text-center border-x border-border">
                        <Bath className="w-5 h-5 text-primary mb-1" />
                        <span className="text-sm font-semibold text-foreground">{property.baths}</span>
                        <span className="text-xs text-muted-foreground font-default">Baths</span>
                      </div>
                      <div className="flex flex-col items-center justify-center text-center">
                        <Maximize className="w-5 h-5 text-primary mb-1" />
                        <span className="text-sm font-semibold text-foreground">{property.sqft}</span>
                        <span className="text-xs text-muted-foreground font-default">Sq Ft</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </CarouselItem>)}
          </CarouselContent>
          
          <div className="flex items-center justify-center gap-4 mt-10 md:hidden">
            <CarouselPrevious className="static translate-y-0 translate-x-0 border-primary text-primary" />
            <CarouselNext className="static translate-y-0 translate-x-0 border-primary text-primary" />
          </div>
          
          <div className="hidden md:block">
             <CarouselPrevious className="-left-12 bg-background border-primary text-primary hover:bg-primary hover:text-primary-foreground shadow-md" />
             <CarouselNext className="-right-12 bg-background border-primary text-primary hover:bg-primary hover:text-primary-foreground shadow-md" />
          </div>
        </Carousel>
        
        <div className="mt-8 text-center md:hidden">
            <Button asChild variant="outline" className="rounded-md w-full border-primary text-primary">
              <Link to="/ResidencesPage">View All Residences</Link>
            </Button>
        </div>
      </div>
    </section>;
}